import { useRef, useState } from 'react';
import { domToSvg, preloadImages, svgToPng } from '../../lib/domToSvg';

/**
 * THE EXPORT MACHINERY, SHARED BY EVERY EXPORT SURFACE.
 *
 * Lifted out of the screen-library page when the creator pack moved to its own
 * route. Both pages need the identical behaviour — the same clipboard
 * fallback, the same font-and-logo wait before capture, the same busy state —
 * and two copies of it would drift the moment one was fixed.
 *
 * Register a frame with `register(id)` on the element to capture, then call
 * `copy` / `save` / `savePng` / `saveAll` with that id.
 */

/**
 * Clipboard write with a fallback.
 *
 * The async Clipboard API refuses to write unless the document is focused, and
 * throws "Document is not focused" if focus moved while the markup was being
 * built — clicking into another pane, or the browser blurring the preview
 * frame. The legacy execCommand path has no such requirement, so it covers the
 * gap: a hidden textarea is focused, selected and copied from directly.
 */
export async function writeToClipboard(text: string) {
  try {
    if (document.hasFocus()) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // Fall through to the legacy path rather than surfacing a focus error.
  }
  const field = document.createElement('textarea');
  field.value = text;
  field.setAttribute('readonly', '');
  field.style.position = 'fixed';
  field.style.top = '0';
  field.style.opacity = '0';
  document.body.appendChild(field);
  field.focus();
  field.select();
  let ok = false;
  try {
    ok = document.execCommand('copy');
  } catch {
    ok = false;
  }
  field.remove();
  return ok;
}
export function download(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
export function downloadSvg(markup: string, filename: string) {
  download(new Blob([markup], {
    type: 'image/svg+xml'
  }), filename);
}
export function useExportFrames() {
  // HTMLElement rather than HTMLDivElement: marks and overlays are captured
  // from a <span> so the artboard is exactly the mark's own box, with no block
  // element adding width around it.
  const frames = useRef<Record<string, HTMLElement | null>>({});
  const [copied, setCopied] = useState<string | null>(null);
  // Capturing is asynchronous — every logo in the frame is fetched and
  // embedded before the markup is handed over — so the button has to say so.
  const [busy, setBusy] = useState<string | null>(null);
  const register = (id: string) => (el: HTMLElement | null) => {
    frames.current[id] = el;
  };
  const capture = async (id: string) => {
    const node = frames.current[id];
    if (!node) return null;
    /*
     * WAIT FOR THE FRAME TO BE FINISHED BEFORE READING IT.
     *
     * The capture is a synchronous walk of computed styles and bounding
     * boxes, so whatever is unfinished at that instant is what lands in the
     * file. Two things are routinely unfinished on a first, un-warmed press:
     *
     *  - Fonts. Inter is a variable webfont; if it has not loaded, every text
     *    layer is measured against the fallback and the export comes back with
     *    the right glyphs at the wrong widths — headings that overhang their
     *    cards, labels that wrap a line early.
     *  - Logos. `preloadImages` inlines them as data URIs; without it the
     *    first export of a frame can miss integration logos entirely.
     *
     * Hovering the controls warms both, but a batch export never hovers, so
     * awaiting here is what makes "Download all" produce the same files as
     * pressing each button by hand.
     */
    if (document.fonts?.status !== 'loaded') await document.fonts.ready;
    /* Some export visuals generate their background nodes after mount. Wait
       until those nodes and their connectors are present; otherwise the PNG
       can contain only the foreground cards even though the preview is whole. */
    const pending = node.querySelectorAll<HTMLElement>('[data-export-ready="false"]');
    if (pending.length) {
      await Promise.all(Array.from(pending).map(element => new Promise<void>(resolve => {
        const started = performance.now();
        const check = () => {
          if (element.dataset.exportReady === 'true' || performance.now() - started > 6000) resolve();
          else requestAnimationFrame(check);
        };
        check();
      })));
    }
    await preloadImages(node);
    // No artboard fill: the frames already carry their own panel or plate, so
    // a second one behind them only shows up as a rectangle you have to crop
    // out. Transparent means the frame drops straight onto any surface.
    return await domToSvg(node, null);
  };
  // Fetch the frame's logos as soon as the pointer reaches its controls, so by
  // the time Copy is pressed the markup is built from cache and the clipboard
  // write happens while the document is still focused.
  const warm = (id: string) => {
    const node = frames.current[id];
    if (node) void preloadImages(node);
  };
  const copy = async (id: string) => {
    setBusy(id);
    try {
      const markup = await capture(id);
      if (!markup) return;
      const ok = await writeToClipboard(markup);
      // If both clipboard paths are unavailable, hand over the file instead of
      // failing silently — the user still gets something to paste from.
      if (!ok) downloadSvg(markup, `giga-${id}.svg`);
      setCopied(id);
      setTimeout(() => setCopied(c => c === id ? null : c), 2000);
    } finally {
      setBusy(b => b === id ? null : b);
    }
  };
  const save = async (id: string) => {
    setBusy(id);
    try {
      const markup = await capture(id);
      if (markup) downloadSvg(markup, `giga-${id}.svg`);
    } finally {
      setBusy(b => b === id ? null : b);
    }
  };
  /**
   * 2× rather than the lockups' 4×: these frames are already 540–1360px wide,
   * so 2× is retina-sharp without producing a file the browser struggles to
   * encode. It is also exactly what puts a 540×960 creator plate on 1080×1920.
   */
  // `name` exists because one frame can ship in more than one shape — the
  // creator plates export square, 9:16 and 16:9 from the same id, and three
  // files called giga-creator-slack.png would overwrite each other.
  const savePng = async (id: string, scale = 2, name = id) => {
    setBusy(id);
    try {
      const markup = await capture(id);
      if (markup) download(await svgToPng(markup, scale), `giga-${name}.png`);
    } finally {
      setBusy(b => b === id ? null : b);
    }
  };
  /**
   * Record a short loop of an animated export frame.
   *
   * The source is live DOM rather than a pre-rendered video, so each sampled
   * frame is passed through the same vector capture used by PNG. This keeps
   * logos, fonts, clipping and DOM paint order identical across formats.
   * WebM is used instead of GIF: it preserves smooth movement at a fraction of
   * the file size and without GIF's 256-colour limit.
   */
  const saveVideo = async (id: string, durationMs = 5000, fps = 10) => {
    const canvas = document.createElement('canvas');
    if (typeof MediaRecorder === 'undefined' || typeof canvas.captureStream !== 'function') return;
    setBusy(id);
    try {
      const firstMarkup = await capture(id);
      if (!firstMarkup) return;
      const width = Number(/width="([\d.]+)"/.exec(firstMarkup)?.[1] ?? 0);
      const height = Number(/height="([\d.]+)"/.exec(firstMarkup)?.[1] ?? 0);
      if (!width || !height) return;
      canvas.width = Math.round(width);
      canvas.height = Math.round(height);
      const context = canvas.getContext('2d');
      if (!context) return;

      const drawMarkup = async (markup: string) => {
        const bitmap = await createImageBitmap(await svgToPng(markup, 1));
        context.clearRect(0, 0, canvas.width, canvas.height);
        context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
        bitmap.close();
      };
      await drawMarkup(firstMarkup);

      const stream = canvas.captureStream(fps);
      const mimeType = ['video/webm;codecs=vp9', 'video/webm;codecs=vp8', 'video/webm'].find(type => MediaRecorder.isTypeSupported(type));
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType, videoBitsPerSecond: 6_000_000 } : { videoBitsPerSecond: 6_000_000 });
      const chunks: BlobPart[] = [];
      recorder.ondataavailable = event => {
        if (event.data.size) chunks.push(event.data);
      };
      const stopped = new Promise<void>((resolve, reject) => {
        recorder.onstop = () => resolve();
        recorder.onerror = () => reject(new Error('video recording failed'));
      });
      recorder.start(500);

      const started = performance.now();
      const frameMs = 1000 / fps;
      while (performance.now() - started < durationMs) {
        const frameStarted = performance.now();
        const markup = await capture(id);
        if (markup) await drawMarkup(markup);
        const remaining = frameMs - (performance.now() - frameStarted);
        if (remaining > 0) await new Promise(resolve => setTimeout(resolve, remaining));
      }
      recorder.stop();
      await stopped;
      stream.getTracks().forEach(track => track.stop());
      download(new Blob(chunks, { type: recorder.mimeType || 'video/webm' }), `giga-${id}.webm`);
    } finally {
      setBusy(b => b === id ? null : b);
    }
  };
  const saveAllPng = async (ids: (string | {
    id: string;
    name: string;
  })[], scale = 2) => {
    for (const entry of ids) {
      const id = typeof entry === 'string' ? entry : entry.id;
      const name = typeof entry === 'string' ? entry : entry.name;
      setBusy(id);
      try {
        const markup = await capture(id);
        if (markup) download(await svgToPng(markup, scale), `giga-${name}.png`);
      } catch {
        // One frame failing should not stop the rest of the batch.
      }
    }
    setBusy(null);
  };
  return {
    register,
    busy,
    copied,
    warm,
    copy,
    save,
    savePng,
    saveVideo,
    saveAllPng
  };
}
